import { useCallback, useRef } from 'react';

import { useSendMessageToClientAgainMutation } from '@app/api/SocialIntegrations';
import { useLeadCardContext } from '@app/components/LeadCard/context';
import { getSendMessageToClientError } from '@app/components/LeadCard/utils';
import { useUserPrint } from '@app/hooks';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';

interface UseMessageSendErrorActionParams {
  clientMessageId: number | null;
  integrationId: number | null;
}

const isValidRetryId = (value: number | null): value is number =>
  typeof value === 'number' && Number.isFinite(value) && value > 0;

export const useMessageSendErrorAction = ({
  clientMessageId,
  integrationId,
}: UseMessageSendErrorActionParams) => {
  const { activeBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const { refreshLatestHistory } = useLeadCardContext();
  const [sendMessageToClientAgain, { isLoading }] =
    useSendMessageToClientAgainMutation();
  const isRetryPendingRef = useRef(false);

  const canRetry =
    isValidRetryId(activeBranchId) &&
    isValidRetryId(integrationId) &&
    isValidRetryId(clientMessageId);

  const retryMessage = useCallback(async (): Promise<boolean> => {
    if (
      isRetryPendingRef.current ||
      !isValidRetryId(activeBranchId) ||
      !isValidRetryId(integrationId) ||
      !isValidRetryId(clientMessageId)
    ) {
      return false;
    }

    isRetryPendingRef.current = true;

    try {
      await sendMessageToClientAgain({
        ActiveBranchId: activeBranchId,
        integrationId,
        clientMessageId,
      }).unwrap();

      // Бэк при повторной отправке не сбрасывает статус сообщения и не шлёт
      // WS-событие — без явного рефетча строка выглядит так, будто «Повторить»
      // не сработала.
      void refreshLatestHistory();

      return true;
    } catch (error) {
      showNotification(getSendMessageToClientError(error));

      return false;
    } finally {
      isRetryPendingRef.current = false;
    }
  }, [
    activeBranchId,
    clientMessageId,
    integrationId,
    refreshLatestHistory,
    sendMessageToClientAgain,
    showNotification,
  ]);

  return {
    canRetry,
    isRetrying: isLoading,
    retryMessage,
  };
};
